import React from 'react';
import { Typography, Accordion, AccordionSummary, AccordionDetails, Box, useTheme, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const FAQ = ({ question, answer }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Accordion sx={{ 
      boxShadow: 'none', 
      background: 'transparent', 
      mb: 1, 
      "&:before": { display: 'none' },
      // Adjust the border radius and padding for small screens
      ...(isSmallScreen ? { borderRadius: '2px' } : { borderRadius: '4px' })
    }}>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />} 
        sx={{ 
          backgroundColor: '#fafafa', 
          borderRadius: '4px', 
          ':hover': { backgroundColor: '#f0f0f0' },
        }}
      >
        <Typography sx={{ fontWeight: 'bold', cursor: 'pointer' }}>{question}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: 'transparent', pt: 2, pb: 2.5 }}>
        <Typography>{answer}</Typography>
      </AccordionDetails>
    </Accordion>
  );
};

const FAQSection = ({ product, validProducts }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  const faqsForProducts = validProducts.reduce((acc, prod) => {
    if (prod === 'Ace with Mocks') {
      acc[prod] = [
        { 
          question: "What can I expect from a mock session?", 
          answer: ( 
            <>
              Mock sessions are 1:1 practice interviews conducted by your selected coach, designed to simulate a 
              real interview experience with someone from your target role and company. Candidates typically 
              book 1-hour sessions, with most using the time for a full-length mock interview, followed by detailed 
              feedback on their responses. Some choose to focus on specific areas, such as the interview process, 
              company culture, or strategies to improve their performance. Each session is designed to help you 
              refine your approach and maximize your chances of interview success.
            </>
          )
        },
        { 
          question: "Can I use my session for general Q&A about the company, its culture, or other job-related topics instead of a mock interview?", 
          answer: ( 
            <>
              If you’d prefer a general discussion, just let your expert know at the start of the session. You can use the time to 
              focus on topics that you believe will best prepare you for your interview.
            </>
          )
        },
        { 
          question: "What if I didn’t get value from my session? Can I get a refund?", 
          answer: ( 
            <>
              Yes, if you’re not satisfied with your session, you can request a refund within 12 hours of the 
              session’s end time. You’ll receive a full refund, minus any payment processing fees charged by our 
              payment provider. Neither the coach nor EastBadge is compensated when a refund is issued. We ask 
              that you submit your refund request promptly, as we need to process coach payments shortly after 
              the session.
            </>
          )
        },
        { 
          question: "Can I cancel my session?", 
          answer: (
            <>
              <Typography>
                Yes, you can cancel your session at any time before the scheduled start. Our refund policy depends on 
                when the cancellation is made:
              </Typography>

              <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                <Typography component="li" sx={{ mt: 1 }}>
                  <strong>If you cancel more than 24 hours before the session:</strong> You’ll receive a full refund, minus 
                  any payment processing fees charged by our provider.
                </Typography>
                <Typography component="li" sx={{ mt: 1 }}>
                  <strong>If you cancel within 24 hours of the session start time:</strong> You’ll receive a 50% refund. 
                  A portion of the remaining fee is used to compensate the coach for the time they had set aside for your 
                  session.
                </Typography>
              </Box>

              <Typography sx={{ mt: 2 }}>
                We ask that you cancel as early as possible — coaches often adjust their personal and professional 
                schedules to accommodate these sessions, and last-minute cancellations can affect how they’ve planned 
                their day. Your consideration helps us maintain a respectful and reliable experience for both candidates 
                and coaches.
              </Typography>
            </>
          )
        },
        {
          question: "Can I reschedule or modify a session?",
          answer: (
            <>
              <Typography>
                Yes, you can request to reschedule or modify your session. While we’re working on adding this feature 
                directly to the platform, please email us at <strong>support@eastbadge.com</strong> with your request.
                Each session may be rescheduled once. Additional reschedule requests may not be supported, as frequent 
                changes create churn for coaches who are balancing these sessions alongside their own professional 
                and personal schedules. Our reschedule policy also depends on when the request is made:
              </Typography>

              <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                <Typography component="li" sx={{ mt: 1 }}>
                  <strong>If you request to reschedule more than 24 hours before the session:</strong> We’ll do our best 
                  to accommodate your request, conditioned on the coach’s availability.
                </Typography>
                <Typography component="li" sx={{ mt: 1 }}>
                  <strong>If you request to reschedule within 24 hours of the session start time:</strong> Coaches often 
                  organize their day around these sessions, and last-minute changes can be difficult to work around. 
                  While we’ll do our best to accommodate the reschedule, the coach is not obligated to accept the change. 
                  If no mutually agreeable time can be found, the session will be treated similar to a cancellation: you will 
                  receive a 50% refund, and the remaining portion will be used to compensate the coach for the time they 
                  had set aside.
                </Typography>
              </Box>

              <Typography sx={{ mt: 2 }}>
                We strongly encourage you to provide as much notice as possible for any schedule changes. This helps us 
                maintain a smooth and dependable experience for both candidates and coaches.
              </Typography>
            </>
          )
        },
        { 
          question: "What if I need to leave the session after it has already started? Can I reschedule the remaining time?", 
          answer: ( 
            <>
              <Typography>
                Once a session has started and both the coach and candidate have joined, the session is considered in progress 
                and cannot be rescheduled—even if it ends early.
              </Typography>

              <Typography sx={{ mt: 2 }}>
                We understand that emergencies can happen, but out of fairness to coaches who prepare and block time specifically 
                for your session, we do not allow reschedules or "carry over" of unused time once the session has begun.
              </Typography>

              <Typography sx={{ mt: 2 }}>
                This policy ensures clarity for both sides and prevents coaches from being placed in difficult situations. 
                If you're unsure about your availability, please reschedule in advance by contacting{' '} 
                <strong>support@eastbadge.com</strong> before the session begins.
              </Typography>
            </>
          )
        },
        { 
          question: "Can I get a first-time discount or a discount for booking multiple sessions?", 
          answer: ( 
            <>
              <Typography>
                No, we do not offer first-time or bulk session discounts. 
              </Typography>

              <Typography sx={{ mt: 2 }}>
                At EastBadge, we focus on keeping our pricing among the lowest compared to similar platforms. 
                We achieve this by optimizing costs across product, marketing, and operations while maintaining 
                minimal margins—often close to zero. As a result, our listed prices are already the best and most 
                competitive we can offer, and we do not provide additional discounts.
              </Typography>

              <Typography sx={{ mt: 2 }}>
                Our goal is to deliver high-quality mock interview experiences at the most accessible rates to help 
                candidates prepare effectively.
              </Typography>
            </>
          ),
        },
        { 
          question: "Can I purchase multiple sessions as a bundle with the same coach to track progress over time?", 
          answer: ( 
            <>
              You can book multiple sessions individually, based on the coach's availability. We don’t offer bundles yet, 
              but if you’d like us to prioritize this feature, contact us at <strong>support@eastbadge.com</strong>.
            </>
          ),
        },
        { 
          question: "What if my question isn’t answered here?", 
          answer: ( 
            <>
              For more answers, visit the {" "}
              <a href="https://www.eastbadge.com/faqs" target="_blank" rel="noopener noreferrer" style={{ color: "#04003F", textDecoration: "underline" }}>
                FAQs
              </a>
              . If you still don’t find what you’re looking for, please reach out to us at 
              <strong>support@eastbadge.com</strong>. We typically respond within a few hours.
            </>
          ),
        },
      ];
    } else if (prod === 'Burnout to Break Free') {
      acc[prod] = [
        { 
          question: "What if my question isn’t answered here?", 
          answer: ( 
            <>
              For more answers, visit the {" "}
              <a href="https://www.eastbadge.com/faqs" target="_blank" rel="noopener noreferrer" style={{ color: "#04003F", textDecoration: "underline" }}>
                FAQs
              </a>
              . If you still don’t find what you’re looking for, please reach out to us at 
              <strong>support@eastbadge.com</strong>. We typically respond within a few hours.
            </>
          ),
        },
      ];
    }
    return acc;
    // ... other products
  }, {});

  // Default FAQs if product not found
  const defaultFaqs = [
    { 
      question: "What if my question isn’t answered here?", 
      answer: ( 
        <>
          For more answers, visit the {" "}
          <a href="https://www.eastbadge.com/faqs" target="_blank" rel="noopener noreferrer" style={{ color: "#04003F", textDecoration: "underline" }}>
            FAQs
          </a>
          . If you still don’t find what you’re looking for, please reach out to us at 
          <strong>support@eastbadge.com</strong>. We typically respond within a few hours.
        </>
      ),
    },
    // ... more default FAQs
  ];

  const faqs = product ? faqsForProducts[product] || defaultFaqs : defaultFaqs;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, mt: 4 }}>FAQs</Typography>
      {faqs.map((faq, index) => (
        <FAQ key={index} question={faq.question} answer={faq.answer} />
      ))}
    </Box>
  );
};

export default FAQSection;